import { useState, useEffect, useRef, useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { useStripePayment } from './useStripePayment'

interface UsePaymentMethodSelectionOptions {
	accountId: number
	billingSystemCountry?: string
	onSuccess: () => void
	paymentMethodType: 'bank' | 'card'
}

export const usePaymentMethodSelection = ({
	accountId,
	billingSystemCountry,
	onSuccess,
	paymentMethodType,
}: UsePaymentMethodSelectionOptions) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const [selectedStripePaymentMethodType, setSelectedStripePaymentMethodType] =
		useState<'CARD' | 'SEPA' | null>(null)
	const hasAutoSelectedRef = useRef(false)

	const {
		initializeStripe,
		stripePromise,
		createCardPaymentIntent,
		createSepaPaymentIntent,
		clientSecret,
		resetPaymentIntent,
		getElementsOptions,
		availablePaymentMethods,
		isLoadingPaymentMethods,
		isCreatingCardIntent,
		isCreatingSepaIntent,
	} = useStripePayment({
		accountId,
		billingSystemCountry,
	})

	const paymentMethodsData = useMemo(() => {
		if (!availablePaymentMethods) return { card: null, sepa: null }

		const methods =
			availablePaymentMethods?.data || availablePaymentMethods || []
		const methodsArray = Array.isArray(methods) ? methods : []

		const cardMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'CARD'
		})

		const sepaMethod = methodsArray.find((method: any) => {
			const methodData = method?.data || method
			return methodData?.payment_type === 'SEPA'
		})

		return { card: cardMethod, sepa: sepaMethod }
	}, [availablePaymentMethods])

	const handleSelectPaymentMethod = useCallback(
		async (paymentPlatformMethodId: number, type: 'CARD' | 'SEPA') => {
			try {
				setSelectedStripePaymentMethodType(type)
				if (!stripePromise) {
					await initializeStripe()
				}

				if (type === 'CARD') {
					await createCardPaymentIntent(paymentPlatformMethodId)
				} else if (type === 'SEPA') {
					await createSepaPaymentIntent(paymentPlatformMethodId)
				}
			} catch (error: any) {
				setSelectedStripePaymentMethodType(null)
				showNotification({
					type: 'error',
					message: t('payment_method.save_error'),
					description: extractErrorMessage(error, t('common.try_again_later')),
				})
				throw error
			}
		},
		[
			stripePromise,
			initializeStripe,
			createCardPaymentIntent,
			createSepaPaymentIntent,
			showNotification,
		]
	)

	// Auto-select CARD if only CARD is available
	useEffect(() => {
		if (
			!isLoadingPaymentMethods &&
			paymentMethodsData.card &&
			!paymentMethodsData.sepa &&
			!selectedStripePaymentMethodType &&
			!clientSecret &&
			!isCreatingCardIntent &&
			paymentMethodType === 'card' &&
			!hasAutoSelectedRef.current
		) {
			const paymentPlatformMethodId =
				paymentMethodsData.card?.id ||
				paymentMethodsData.card?.paymentPlatformId
			if (paymentPlatformMethodId) {
				hasAutoSelectedRef.current = true
				handleSelectPaymentMethod(paymentPlatformMethodId, 'CARD').catch(() => {
					hasAutoSelectedRef.current = false
				})
			}
		}

		// Reset ref when switching payment types or when conditions change
		if (
			paymentMethodType !== 'card' ||
			selectedStripePaymentMethodType ||
			clientSecret
		) {
			hasAutoSelectedRef.current = false
		}
	}, [
		isLoadingPaymentMethods,
		paymentMethodsData.card,
		paymentMethodsData.sepa,
		selectedStripePaymentMethodType,
		clientSecret,
		isCreatingCardIntent,
		paymentMethodType,
		handleSelectPaymentMethod,
	])

	const handleStripeCardSubmit = useCallback(
		async (_paymentIntentId: string, _paymentMethodId: string) => {
			// Stripe has already confirmed the payment
			// The webhook will handle saving the payment method to the backend
			// Just show success notification and call onSuccess callback
			showNotification({
				type: 'success',
				message: t('payment_method.save_success'),
			})

			// Wait 3 seconds to give webhook time to process
			await new Promise((resolve) => setTimeout(resolve, 3000))

			onSuccess()
		},
		[showNotification, t, onSuccess]
	)

	const handleCancel = useCallback(() => {
		// If a payment method was selected (clientSecret exists) and both methods are available,
		// reset the selection to show the selector again
		if (clientSecret && paymentMethodsData.card && paymentMethodsData.sepa) {
			// Reset selection - user will see the selector again
			setSelectedStripePaymentMethodType(null)
			resetPaymentIntent()
		} else {
			// If no selection was made or only one method available, go back to list
			onSuccess() // This goes back because it triggers the parent's onSuccess which is handleBackToList
		}
	}, [
		clientSecret,
		paymentMethodsData.card,
		paymentMethodsData.sepa,
		onSuccess,
		resetPaymentIntent,
	])

	return {
		stripePromise,
		clientSecret,
		getElementsOptions,
		availablePaymentMethods,
		isLoadingPaymentMethods,
		isCreatingCardIntent,
		isCreatingSepaIntent,
		selectedStripePaymentMethodType,
		handleSelectPaymentMethod,
		handleStripeCardSubmit,
		handleCancel,
	}
}
